import { IoIosRemoveCircleOutline } from '@react-icons/all-files/io/IoIosRemoveCircleOutline'
import { useState } from 'react'
import { MonacoTextEditor } from '~/components/editors/monaco/MonacoTextEditor'
import { SourceOutputExamples } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { mapSourceDataTypeToLanguage } from './examples'

interface SamplesListProps {
  samples: SourceOutputExamples[]
  sourceDataType: string
  onSamplesChange: (samples: SourceOutputExamples[]) => void
  handleEditorSourceChange?: (value: string) => void
  handleEditorOutputChange?: (value: string) => void
  onSourceDataValidationError: (hasError: boolean) => void
  onOutputDataValidationError: (hasError: boolean) => void
  disabled?: boolean
}

export function SamplesList({
  samples,
  sourceDataType,
  onSamplesChange,
  handleEditorSourceChange,
  handleEditorOutputChange,
  onSourceDataValidationError,
  onOutputDataValidationError,
  disabled = false
}: SamplesListProps) {
  const isPlugin = bridge.isInPlugin()
  const [sampleInputErrors, setSampleInputErrors] = useState<string[]>(
    Array.from({ length: samples.length }, () => '')
  )
  const [sampleOutputErrors, setSampleOutputErrors] = useState<string[]>(
    Array.from({ length: samples.length }, () => '')
  )

  const handleExampleDelete = (index: number) => {
    onSamplesChange(samples.filter((_, i) => i !== index))
  }

  return (
    <div>
      {samples.map((example, index) => (
        <div
          className={`${isPlugin ? 'flex flex-col' : 'flex'} w-full h-full relative`}
          key={index}
        >
          <div
            className={`tool-tip-target input-example w-[1px] h-[1px] relative top-[200px] ${isPlugin ? 'left-[150px]' : 'left-[200px]'}`}
          ></div>
          <div
            className={`tool-tip-target output-example w-[1px] h-[1px] relative top-[200px] ${isPlugin ? 'left-[150px]' : 'left-[600px]'}`}
          ></div>
          <div className={`input-example ${isPlugin ? 'w-full' : 'w-1/2'} flex-grow mb-4`}>
            {index === 0 ? (
              <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[5px] select-none'>
                Sample Input Data:
              </div>
            ) : null}
            <MonacoTextEditor
              value={example.sourceData}
              language={mapSourceDataTypeToLanguage(sourceDataType)}
              onChange={value => {
                const newExamples = [...samples]
                newExamples[index].sourceData = value
                handleEditorSourceChange?.(value)
                onSamplesChange(newExamples)
              }}
              onError={() => onSourceDataValidationError(true)}
              onErrorClear={() => onSourceDataValidationError(false)}
              disabled={disabled}
            />
            {sampleInputErrors[index] && (
              <div className='text-red-500 text-xs mt-1'>{sampleInputErrors[index]}</div>
            )}
            {index > 0 && !disabled && (
              <button
                onClick={() => handleExampleDelete(index)}
                className='absolute -right-8 top-0 p-1 text-gray-500 hover:text-red-500 transition-colors'
                aria-label='Delete example'
              >
                <IoIosRemoveCircleOutline size={24} />
              </button>
            )}
          </div>
          <div className={`output-example ${isPlugin ? 'w-full' : 'w-1/2'} flex-grow mb-4`}>
            {index === 0 ? (
              <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[5px] select-none'>
                Sample Output Data:
              </div>
            ) : null}
            <MonacoTextEditor
              value={example.outputData}
              className='border-l-0'
              onChange={value => {
                const newExamples = [...samples]
                newExamples[index].outputData = value
                handleEditorOutputChange?.(value)
                onSamplesChange(newExamples)
              }}
              onError={() => onOutputDataValidationError(true)}
              onErrorClear={() => onOutputDataValidationError(false)}
              requireLangs={
                ['DATA_TYPE_EDI', 'DATA_TYPE_EDIFACT', 'DATA_TYPE_HL7'].includes(sourceDataType)
                  ? ['xml', 'json']
                  : []
              }
              disabled={disabled}
            />
          </div>
          {sampleOutputErrors[index] && (
            <div className='text-red-500 text-xs mt-1'>{sampleOutputErrors[index]}</div>
          )}
        </div>
      ))}
    </div>
  )
}
